import React, { useMemo } from 'react';
import { FilmSlateIcon, UserIcon, ChatCircleIcon, ClockIcon, FileTextIcon, XIcon } from '@phosphor-icons/react';
import { ScriptBlockData } from './ScriptBlock';
import { estimatePageCount } from './usePagination';
import { Button } from '../ui/button';
import {scriptDialogueStatistics} from '../../lib/scriptStatistics';

interface ScriptStatsProps {
    blocks: ScriptBlockData[];
    onClose: () => void;
}

export const ScriptStats: React.FC<ScriptStatsProps> = ({ blocks, onClose }) => {
    const stats = useMemo(() => {
        // Scene count
        const scenes = blocks.filter(b => b.type === 'scene-heading');

        const {characterDialogue,totalDialogueLines,totalWords}=scriptDialogueStatistics(blocks);

        // Sort characters by dialogue lines
        const sortedCharacters = Object.entries(characterDialogue)
            .sort(([, a], [, b]) => b.lines - a.lines)
            .slice(0, 10); // Top 10

        // Total dialogue

        const totalActionLines = blocks.filter(b => b.type === 'action').length;

        // Page count
        const pageCount = estimatePageCount(blocks);

        // Estimated runtime (1 page ≈ 1 minute)
        const estimatedMinutes = pageCount;

        // Location analysis
        const interiors = scenes.filter(s => s.content.toUpperCase().startsWith('INT.')).length;
        const exteriors = scenes.filter(s => s.content.toUpperCase().startsWith('EXT.')).length;

        return {
            sceneCount: scenes.length,
            pageCount,
            estimatedMinutes,
            totalDialogueLines,
            totalActionLines,
            characterCount: Object.keys(characterDialogue).length,
            sortedCharacters,
            interiors,
            exteriors,
            totalWords,
            totalBlocks: blocks.length
        };
    }, [blocks]);

    const formatTime = (minutes: number) => {
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    return (
        <div className="w-72 h-full bg-zinc-900 border-l border-white/10 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <FileTextIcon className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-zinc-200">Script Stats</span>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="h-6 w-6 p-0 text-zinc-500 hover:text-white"
                >
                    <XIcon className="w-3.5 h-3.5" />
                </Button>
            </div>

            {/* Stats Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-4">
                {/* Overview */}
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-zinc-100">{stats.pageCount}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Pages</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-zinc-100">{stats.sceneCount}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Scenes</div>
                    </div>
                </div>

                {/* Runtime */}
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <ClockIcon className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-medium text-emerald-300">Est. Runtime</span>
                    </div>
                    <div className="text-xl font-bold text-emerald-100">
                        {formatTime(stats.estimatedMinutes)}
                    </div>
                    <div className="text-[10px] text-emerald-400/60 mt-1">
                        Based on 1 page = 1 minute rule
                    </div>
                </div>

                {/* Breakdown */}
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Breakdown</div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Action Lines</span>
                            <span className="text-zinc-200 font-medium">{stats.totalActionLines}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Dialogue Lines</span>
                            <span className="text-zinc-200 font-medium">{stats.totalDialogueLines}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">INT. Scenes</span>
                            <span className="text-zinc-200 font-medium">{stats.interiors}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">EXT. Scenes</span>
                            <span className="text-zinc-200 font-medium">{stats.exteriors}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Total Words</span>
                            <span className="text-zinc-200 font-medium">{stats.totalWords.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* Characters */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <UserIcon className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                            Characters ({stats.characterCount})
                        </span>
                    </div>

                    {stats.sortedCharacters.length === 0 ? (
                        <div className="text-xs text-zinc-600 italic">No dialogue yet</div>
                    ) : (
                        <div className="space-y-1">
                            {stats.sortedCharacters.map(([name, data], index) => (
                                <div key={name} className="flex items-center gap-2 group">
                                    <span className="text-[10px] font-mono text-zinc-600 w-4">{index + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-zinc-300 truncate font-medium">
                                                {name}
                                            </span>
                                            <span className="text-[10px] text-zinc-500">
                                                {data.lines} lines
                                            </span>
                                        </div>
                                        {/* Word bar */}
                                        <div className="mt-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-indigo-500/50 rounded-full"
                                                style={{
                                                    width: `${Math.min(100, (data.words / (stats.sortedCharacters[0]?.[1]?.words || 1)) * 100)}%`
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-white/5 bg-white/[0.02]">
                <div className="text-[10px] text-zinc-600 text-center">
                    Stats update as you write
                </div>
            </div>
        </div>
    );
};
